import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { notify } from '../../helpers/toastify';
import { useMutation, useQuery } from '@apollo/client';

import { GET_ONE_ACTIVITY, ACTIVITY_UPSERT, ACTIVITIES_FOR_ONE_MISSION } from '@bloomays-lib/adapter.api-bloomer';

import AddCRA from '../organisms/AddCRA';
import { AirtableActivity, ActivityDayDetails, IActivity, ActivityDayType } from '@bloomays-lib/types.shared';
import { calculPerDay } from '../../helpers/CRA';
import { errorLogger, getApolloErrorCode } from '../../helpers/error';
import { LoaderSkeleton, WithErrorBoundary } from '@bloomays-lib/ui.shared';
import { convertActivityToEachDaysArrayWithMissionBoundaries, createDaysDetail } from '@bloomays-lib/utils.shared';

const EditCRA = () => {
  const { missionRecordId, date } = useParams<{
    missionRecordId: string;
    date: string;
  }>();

  if (!date) {
    throw new Error('Missing date params');
  }

  const [upsertActivity] = useMutation<{ upsertActivity: AirtableActivity }>(ACTIVITY_UPSERT, {
    refetchQueries: [
      {
        query: GET_ONE_ACTIVITY,
        variables: { month: date, missionRecordId: missionRecordId },
      },
      {
        query: ACTIVITIES_FOR_ONE_MISSION,
        variables: { recordId: missionRecordId },
      },
    ],
  });
  const [, setResponsePostCRA] = useState('');
  const [activity, setActivity] = useState<IActivity | undefined>();
  const [hoursPerDay, setHoursPerDay] = useState<number>(8);
  const [eachDay, setEachDay] = useState<ActivityDayDetails[]>([]);
  const navigate = useNavigate();

  const { data, error, loading } = useQuery<{ activities: IActivity[] }>(GET_ONE_ACTIVITY, {
    variables: {
      month: date,
      missionRecordId: missionRecordId,
    },
  });

  const submitCRA = async (items: ActivityDayDetails[]): Promise<void> => {
    let result;
    const details = createDaysDetail(items);
    const day = calculPerDay(items, ActivityDayType.nbDaysWorked);
    const dayAstreinte = calculPerDay(items, ActivityDayType.astreinte);
    const dayMarkup = calculPerDay(items, ActivityDayType.nbDaysMarkupWorked);
    const dayMarkup2 = calculPerDay(items, ActivityDayType.nbDaysMarkup2Worked);

    if (!missionRecordId) {
      notify(
        'info',
        "Il semblerait qu'il manque des données sur la mission, peux-tu re sélectionner la mission s'il te plaît ?",
      );
      return;
    }
    setResponsePostCRA('loading');
    try {
      const inputVar: any = {
        month: activity?.month,
        nbDaysWorked: day,
        nbDaysMarkupWorked: dayMarkup,
        nbDaysMarkup2Worked: dayMarkup2,
        missionRecordId: missionRecordId,
        nbDaysDetails: JSON.stringify(details),
        astreinte: dayAstreinte,
        signedCount: activity?.signedCount || 0,
        hoursPerDays: hoursPerDay,
      };
      if (activity?.recordId) {
        inputVar.recordId = activity.recordId;
      }
      result = await upsertActivity({
        variables: {
          input: inputVar,
        },
      });
      if (result.data) {
        setResponsePostCRA('posted');
      }
      navigate(`/mission/${missionRecordId}`);
    } catch (error: any) {
      setResponsePostCRA('not posted');
      errorLogger(error, {
        extraInfos: 'Error adding / edit CRA',
        state: activity,
      });
      notify('error', "Souci avec l'ajout du CRA, reviens plus tard 😓 ", error);
      throw error;
    }
  };

  useEffect(() => {
    const acti = data?.activities?.[0];
    if (!acti) {
      return;
    }
    setHoursPerDay(acti.hoursPerDays);
    setActivity(acti);
  }, [data]);

  useEffect(() => {
    if (!activity) {
      return;
    }
    const result: ActivityDayDetails[] = convertActivityToEachDaysArrayWithMissionBoundaries(
      activity.month,
      activity.nbDaysDetails,
      {
        beginMission: new Date(activity.mission!.beginMission as string),
        endMission: new Date(activity.mission!.endMission as string),
      },
    );
    setEachDay(result);
  }, [activity]);

  useEffect(() => {
    if (!error) {
      return;
    }
    let textError;
    const errorCode = getApolloErrorCode(error);
    switch (errorCode) {
      case 'UNKNOWN_RECORD_ID':
        textError = "Cette mission n'existe pas";
        break;
      case 'OBJECT_NOT_OWNED':
        textError = "L'id de cette mission ne semble pas vous appartenir";
        break;
      default:
        textError = 'une erreur non identifiée est survenue';
    }
    notify('error', 'Impossible de récupérer tes données. Erreur du server, essaie plus tard ! 😓 ', textError);
  }, [error]);

  if (loading) {
    return <LoaderSkeleton height={500} width={1200} />;
  }

  return (
    <div>
      <AddCRA
        readOnly={activity?.signing || activity?.validated}
        month={activity?.month || date}
        eachDay={eachDay}
        submitCRA={submitCRA}
        hoursPerDay={hoursPerDay}
      />
    </div>
  );
};

export default WithErrorBoundary(EditCRA);
